import { useState } from "react";
import { decideSandboxToolApproval } from "@/api/sandbox";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/useToast";
import type { SandboxPendingApproval } from "@/types/sandbox";

// "web_search" -> "Web search"
function humanize(name: string) {
  const words = name.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function displayValue(value: unknown) {
  return typeof value === "string" ? value : JSON.stringify(value);
}

type Props = {
  conversationId: number;
  approvals: SandboxPendingApproval[];
  /** Called with the calls still waiting once the server has recorded a decision. */
  onChange: (approvals: SandboxPendingApproval[]) => void;
};

/** One card per tool call the agent has paused on. Nothing runs until the person decides. */
export function SandboxPendingApprovals({ conversationId, approvals, onChange }: Props) {
  return (
    <>
      {approvals.map((approval) => (
        <ApprovalCard key={approval.id} conversationId={conversationId} approval={approval} onChange={onChange} />
      ))}
    </>
  );
}

function ApprovalCard({ conversationId, approval, onChange }: Omit<Props, "approvals"> & { approval: SandboxPendingApproval }) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toolName = humanize(approval.name);

  const decide = async (approved: boolean) => {
    setError(null);
    setIsSaving(true);
    try {
      const { pending_approvals } = await decideSandboxToolApproval(conversationId, approval.id, approved);
      // A denial must not read as a finished search.
      if (!approved) toast({ title: "Request denied", description: `${toolName} did not run.` });
      onChange(pending_approvals);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Your decision could not be saved. Please try again.");
      setIsSaving(false);
    }
  };

  return (
    <Card role="group" aria-label={`Approval needed: ${toolName}`} className="border-amber-300 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/30">
      <CardContent className="space-y-3 p-3 text-sm">
        <div>
          <p className="font-medium">Approval needed: {toolName}</p>
          <p className="text-muted-foreground">Nothing runs until you approve.</p>
        </div>
        <dl className="space-y-1">
          {Object.entries(approval.arguments).map(([key, value]) => (
            <div key={key} className="flex gap-2">
              <dt className="shrink-0 text-muted-foreground">{humanize(key)}</dt>
              <dd className="min-w-0 break-words font-medium">{displayValue(value)}</dd>
            </div>
          ))}
        </dl>
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <Button type="button" size="sm" onClick={() => void decide(true)} disabled={isSaving}>
            Approve
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => void decide(false)} disabled={isSaving}>
            Deny
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
